// ===========================================================================
// frontend/src/screens/CropListing/Photos.jsx
// NEW UI — Photos Step (Step 3 of 4)
// Reference: Figma design — "Show the quality of your produce"
// ===========================================================================

import React, { useState, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    useTheme,
    useMediaQuery,
    Grid,
} from '@mui/material';
import {
    IconCheck,
    IconX,
    IconCloudUpload,
    IconPhoto,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { colors, fonts } from '../../ColorFont/color';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import { url } from '../../ConstUrl/Url';

// ─── Constants ───────────────────────────────────────────────────────────────
const PRIMARY = colors.primary;
const PRIMARY_DARK = colors.primary_dark || '#149343';

// ─── Translations ─────────────────────────────────────────────────────────────
const photosTranslations = {
    en: {
        pageTitle: 'Add New Crop',
        breadcrumb: { dashboard: 'Dashboard', cropListings: 'Crop Listings', createNew: 'Create New' },
        steps: {
            cropCategory: 'Crop Category',
            varietyGrade: 'Variety & Grade', 
            pricing: 'Pricing',
            photos: 'Photos',
            locationDelivery: 'Location & Delivery',
        },
        cardTitle: 'Show the quality of your produce',
        cardSubtitle: 'Upload clear images to increase buyer confidence and improve inquiries.',
        imagesLabel: 'Images',
        dropSelect: 'Drop or select file',
        dropOrClick: 'Drop files here or click to ',
        browse: 'browse',
        throughMachine: ' through your machine.',
        back: 'Back',
        next: 'Next',
    },
    hi: {
        pageTitle: 'नई फसल जोड़ें',
        breadcrumb: { dashboard: 'डैशबोर्ड', cropListings: 'फसल सूची', createNew: 'नया बनाएं' },
        steps: {
            cropCategory: 'फसल श्रेणी',
            varietyGrade: 'किस्म और ग्रेड',
            pricing: 'मूल्य',
            photos: 'फोटो',
            locationDelivery: 'स्थान और डिलीवरी',
        },
        cardTitle: 'अपनी उपज की गुणवत्ता दिखाएं',
        cardSubtitle: 'खरीदारों का विश्वास बढ़ाने और पूछताछ में सुधार करने के लिए स्पष्ट चित्र अपलोड करें।',
        imagesLabel: 'चित्र',
        dropSelect: 'फाइल छोड़ें या चुनें',
        dropOrClick: 'फाइलें यहां छोड़ें या ',
        browse: 'ब्राउज़',
        throughMachine: ' करने के लिए क्लिक करें।',
        back: 'वापस',
        next: 'अगला',
    },
    mr: {
        pageTitle: 'नवीन पीक जोडा',
        breadcrumb: { dashboard: 'डॅशबोर्ड', cropListings: 'पीक सूची', createNew: 'नवीन तयार करा' },
        steps: {
            cropCategory: 'पीक श्रेणी',
            varietyGrade: 'वाण आणि दर्जा',
            pricing: 'किंमत',
            photos: 'फोटो',
            locationDelivery: 'स्थान आणि डिलिव्हरी',
        },
        cardTitle: 'तुमच्या उत्पादनाची गुणवत्ता दाखवा',
        cardSubtitle: 'खरेदीदारांचा आत्मविश्वास वाढवण्यासाठी आणि चौकशी सुधारण्यासाठी स्पष्ट चित्रे अपलोड करा.',
        imagesLabel: 'चित्रे',
        dropSelect: 'फाइल ड्रॉप करा किंवा निवडा',
        dropOrClick: 'फाइल्स येथे ड्रॉप करा किंवा ',
        browse: 'ब्राउझ',
        throughMachine: ' करण्यासाठी क्लिक करा.',
        back: 'मागे',
        next: 'पुढे',
    },
};

// ─── Stepper Component ────────────────────────────────────────────────────────
const stepperData = (tLocal) => [
    { label: tLocal.steps.cropCategory, state: 'done', step: 1 },
    { label: tLocal.steps.varietyGrade, state: 'done', step: 2 },
    { label: tLocal.steps.pricing, state: 'done', step: 3 },
    { label: tLocal.steps.photos, state: 'active', step: 4 },
    { label: tLocal.steps.locationDelivery, state: 'inactive', step: 5 },
];

const Stepper = ({ steps }) => {
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
            mb: { xs: 3, md: 5 },
            position: 'relative',
        }}>
            {/* Background connector line */}
            <Box sx={{
                position: 'absolute',
                top: 18,
                left: '10%',
                right: '10%',
                height: '2px',
                backgroundColor: '#E0E5EB',
                zIndex: 0,
            }} />

            {steps.map((step, idx) => {
                const isDone = step.state === 'done';
                const isActive = step.state === 'active';
                const isInactive = step.state === 'inactive';

                return (
                    <Box
                        key={idx}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flex: 1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        {/* Circle */}
                        <Box sx={{
                            width: { xs: 32, md: 36 },
                            height: { xs: 32, md: 36 },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isDone ? PRIMARY : isActive ? PRIMARY : '#E0E5EB',
                            border: isActive ? `3px solid ${PRIMARY}` : isInactive ? '2px solid #C4CDD5' : 'none',
                            boxShadow: isActive ? `0 0 0 4px ${PRIMARY}22` : 'none',
                            transition: 'all 0.3s ease',
                        }}>
                            {isDone ? (
                                <IconCheck size={18} color="#FFFFFF" stroke={3} />
                            ) : (
                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: { xs: '13px', md: '14px' },
                                    color: isActive ? '#FFFFFF' : '#919EAB',
                                }}>
                                    {step.step}
                                </Typography>
                            )}
                        </Box>

                        {/* Label */}
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: isActive ? 700 : isDone ? 600 : 500,
                            fontSize: { xs: '11px', md: '13px' },
                            color: isActive ? '#1C252E' : isDone ? '#1C252E' : '#919EAB',
                            mt: 1,
                            textAlign: 'center',
                            maxWidth: { xs: '72px', sm: '100px' },
                            lineHeight: 1.3,
                        }}>
                            {step.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Photos = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const langCode = i18n.language || 'en';
    const currentLang = langCode.startsWith('hi') ? 'hi' : (langCode.startsWith('mr') || langCode.startsWith('mar')) ? 'mr' : 'en';
    const tLocal = photosTranslations[currentLang];

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // ─── Form State ────────────────────────────────────────────────────────────
    const location = useLocation();
    const existingData = location.state?.cropData || {};
    const [images, setImages] = useState(() => {
        if (existingData.photos && existingData.photos.length > 0) {
            return existingData.photos.map(p => {
                if (typeof p === 'string') {
                    return { file: p, preview: p.startsWith('http') ? p : `${url}/${p}` };
                }
                // Handle File objects if they come from a previous step's state
                try {
                    return { file: p, preview: URL.createObjectURL(p) };
                } catch (e) {
                    return { file: p, preview: p };
                }
            });
        }
        return [];
    });
    const fileInputRef = useRef(null);
    const uploadRef = useRef(null);
    const [error, setError] = useState(false);

    const steps = stepperData(tLocal);

    // Localized validation messages
    const valMsgs = {
        en: "Please upload at least one photo of your produce",
        hi: "कृपया अपने उत्पाद की कम से कम एक फोटो अपलोड करें",
        mr: "कृपया तुमच्या उत्पादनाचा किमान एक फोटो अपलोड करा"
    };
    const langCodeVal = i18n.language || 'en';
    const currentLangVal = langCodeVal.startsWith('hi') ? 'hi' : (langCodeVal.startsWith('mr') || langCodeVal.startsWith('mar')) ? 'mr' : 'en';
    const tVal = valMsgs[currentLangVal] || valMsgs.en;

    const handleNext = () => {
        if (images.length === 0) {
            setError(true);
            uploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        const updatedCropData = {
            ...(location.state?.cropData || {}),
            photos: images.map(img => img.file),
        };
        navigate('/location-delivery', { 
            state: { 
                cropData: updatedCropData,
                isEdit: location.state?.isEdit,
                cropId: location.state?.cropId
            } 
        }); 
    };
    const handleBack = () => {
        const updatedCropData = {
            ...(location.state?.cropData || {}),
            photos: images.map(img => img.file),
        };
        navigate('/pricing', { 
            state: { 
                cropData: updatedCropData,
                isEdit: location.state?.isEdit,
                cropId: location.state?.cropId
            } 
        });
    };

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const currentCount = images.length;
            const remaining = 5 - currentCount;
            
            if (files.length > remaining) {
                toast.error(`Maximum 5 images allowed. You can only add ${remaining} more.`);
            }

            const filesArray = Array.from(files).slice(0, remaining);
            if (filesArray.length > 0) {
                setError(false);
            }
            const newImages = filesArray.map(file => ({
                file: file,
                preview: URL.createObjectURL(file)
            }));
            setImages(prev => [...prev, ...newImages]);
        }
    };

    const handleRemoveImage = (index) => {
        const updated = images.filter((_, i) => i !== index);
        setImages(updated);
        if (updated.length === 0) {
            // Optional: you can show error again or leave it blank
        }
    };

    return (
        <Box sx={{
            minHeight: '100%',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
                {/* ── Page Title + Breadcrumb ── */}
                <Box sx={{
                    width: '100%',
                    maxWidth: '900px',
                    px: 0,
                    pt: { xs: 3, md: 4 },
                    pb: { xs: 1, md: 2 },
                }}>
                    <Typography sx={{
                        fontFamily: fonts.poppins,
                        fontWeight: 800,
                        fontSize: { xs: '22px', md: '28px' },
                        color: '#1C252E',
                        mb: 1,
                    }}>
                        {location.state?.isEdit ? (currentLang === 'hi' ? 'फसल संपादित करें' : currentLang === 'mr' ? 'पीक संपादित करा' : 'Edit Crop') : tLocal.pageTitle}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 3, md: 4 } }}>
                        {[tLocal.breadcrumb.dashboard, tLocal.breadcrumb.cropListings, location.state?.isEdit ? (currentLang === 'hi' ? 'संपादित करें' : currentLang === 'mr' ? 'संपादित करा' : 'Edit Listing') : tLocal.breadcrumb.createNew].map((crumb, idx, arr) => (
                            <React.Fragment key={idx}>
                                <Typography sx={{
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontSize: '13px',
                                    fontWeight: idx === arr.length - 1 ? 600 : 400,
                                    color: idx === arr.length - 1 ? '#1C252E' : '#637381',
                                    cursor: idx < arr.length - 1 ? 'pointer' : 'default',
                                }}>
                                    {crumb}
                                </Typography>
                                {idx < arr.length - 1 && (
                                    <Box sx={{
                                        width: '5px', height: '5px', borderRadius: '50%',
                                        backgroundColor: '#637381'
                                    }} />
                                )}
                            </React.Fragment>
                        ))}
                    </Box>

                    {/* ── Stepper ── */}
                    <Stepper steps={steps} />
                </Box>

                {/* ── Main Card ── */}
                <Box sx={{
                    width: '100%',
                    maxWidth: '900px',
                    px: 0,
                    pb: { xs: 4, md: 6 },
                }}>
                    <Box sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: { xs: '16px', md: '20px' },
                        border: '1px solid #E0E5EB',
                        boxShadow: '0px 4px 24px rgba(0,0,0,0.04)',
                        px: { xs: 3, md: 6 },
                        py: { xs: 4, md: 5 },
                    }}>
                        {/* Card heading */}
                        <Typography sx={{
                            fontFamily: fonts.poppins,
                            fontWeight: 800,
                            fontSize: { xs: '20px', md: '24px' },
                            color: '#1C252E',
                            textAlign: 'center',
                            mb: 1,
                        }}>
                            {tLocal.cardTitle}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: { xs: '13px', md: '14px' },
                            color: '#637381',
                            textAlign: 'center',
                            mb: { xs: 4, md: 5 },
                            maxWidth: '520px',
                            mx: 'auto',
                            lineHeight: 1.6,
                        }}>
                            {tLocal.cardSubtitle}
                        </Typography>

                        {/* ── Upload Area ── */}
                        <Box ref={uploadRef} sx={{ mb: 4 }}>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '14px',
                                color: error ? '#FF4842' : '#1C252E',
                                mb: 1.5,
                            }}>
                                {tLocal.imagesLabel}
                            </Typography>

                            <Box 
                                onClick={handleUploadClick}
                                sx={{
                                    border: `1px dashed ` + (error ? '#FF4842' : '#C4CDD5'),
                                    borderRadius: '12px',
                                    backgroundColor: '#FFFFFF',
                                    py: { xs: 6, md: 8 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: error ? '#FF4842' : PRIMARY,
                                        backgroundColor: colors.primary_rgba_4,
                                    }
                                }}
                            >
                                {/* Hidden Input */}
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                {/* Illustration / Icon */}
                                <Box sx={{ mb: 2, position: 'relative' }}>
                                    <Box
                                        component="img"
                                        src="/assets/icons/upload-folder.png"
                                        alt="upload"
                                        sx={{ width: 120, height: 'auto', display: 'block', filter: error ? 'grayscale(1) sepia(1) hue-rotate(-50deg) saturate(5)' : 'none' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    {/* Fallback Icon if image fails */}
                                    <IconCloudUpload size={64} color={error ? '#FF4842' : PRIMARY} stroke={1.5} style={{ display: 'none' }} className="fallback-icon" />
                                </Box>

                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: { xs: '16px', md: '18px' },
                                    color: error ? '#FF4842' : '#1C252E',
                                    mb: 1
                                }}>
                                    {tLocal.dropSelect}
                                </Typography>

                                <Typography sx={{
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontSize: '14px',
                                    color: '#637381',
                                    textAlign: 'center',
                                    px: 2
                                }}>
                                    {tLocal.dropOrClick}
                                    <Typography component="span" sx={{
                                        color: error ? '#FF4842' : PRIMARY,
                                        fontWeight: 600,
                                        textDecoration: 'underline',
                                        fontFamily: 'inherit',
                                        fontSize: 'inherit'
                                    }}>
                                        {tLocal.browse}
                                    </Typography>
                                    {tLocal.throughMachine}
                                </Typography>
                            </Box>
                            {error && (
                                <Typography sx={{
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontSize: '13px',
                                    color: '#FF4842',
                                    fontWeight: 600,
                                    mt: 1.5,
                                    textAlign: 'center'
                                }}>
                                    {tVal}
                                </Typography>
                            )}
                        </Box>

                        {/* ── Image Previews ── */}
                        {images.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 5 }}>
                                {images.map((item, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            position: 'relative',
                                            width: 80,
                                            height: 80,
                                            borderRadius: '12px',
                                            border: '1px solid #E0E5EB',
                                            p: 0.5,
                                            backgroundColor: '#FFFFFF',
                                            overflow: 'visible'
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={item.preview}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '8px'
                                            }}
                                        />
                                        <IconButton
                                            onClick={() => handleRemoveImage(idx)}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: -8,
                                                right: -8,
                                                backgroundColor: 'rgba(28, 37, 46, 0.7)',
                                                color: '#FFFFFF',
                                                padding: '2px',
                                                '&:hover': { backgroundColor: '#1C252E' }
                                            }}
                                        >
                                            <IconX size={14} stroke={3} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* ── Back + Next Buttons ── */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 2, md: 3 },
                            justifyContent: 'center',
                            flexDirection: { xs: 'column-reverse', sm: 'row' },
                        }}>
                            <Button
                                variant="outlined"
                                onClick={handleBack}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: '50px',
                                    px: { xs: 3, md: 6 },
                                    height: '52px',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    textTransform: 'none',
                                    color: '#1C252E',
                                    borderColor: '#C4CDD5',
                                    minWidth: { sm: '180px' },
                                    '&:hover': {
                                        borderColor: '#919EAB',
                                        backgroundColor: '#F4F6F8',
                                    },
                                }}
                            >
                                {tLocal.back}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                fullWidth={isMobile}
                                sx={{
                                    borderRadius: '50px',
                                    px: { xs: 3, md: 6 },
                                    height: '52px',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    textTransform: 'none',
                                    backgroundColor: PRIMARY,
                                    color: '#FFFFFF',
                                    minWidth: { sm: '200px' },
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: PRIMARY_DARK,
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {tLocal.next}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
    );
};

export default Photos;
